import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [operators, setOperators] = useState([]);
  const [activeOperator, setActiveOperatorState] = useState(() => {
    try {
      const raw = sessionStorage.getItem('activeOperator');
      return raw ? JSON.parse(raw) : null;
    } catch (err) {
      sessionStorage.removeItem('activeOperator');
      return null;
    }
  });
  const [loading, setLoading] = useState(true);
  const userRef = useRef(user);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const setActiveOperator = useCallback((operator) => {
    if (operator) {
      sessionStorage.setItem('activeOperator', JSON.stringify(operator));
      setActiveOperatorState(operator);
    } else {
      sessionStorage.removeItem('activeOperator');
      setActiveOperatorState(null);
    }
  }, []);

  const loadOperators = useCallback(async (accountUser) => {
    try {
      const targetUser = accountUser || userRef.current;
      if (targetUser?.role === 'trabajador') {
        setOperators([]);
        setActiveOperator(null);
        return [];
      }
      const { data } = await axios.get(`${BACKEND_URL}/api/operators`, {
        withCredentials: true
      });
      setOperators(data || []);
      const stored = (() => {
        try {
          const raw = sessionStorage.getItem('activeOperator');
          return raw ? JSON.parse(raw) : null;
        } catch (err) {
          return null;
        }
      })();
      if ((data || []).length === 1) {
        setActiveOperator(data[0]);
      } else if (stored && (data || []).some(op => op.id === stored.id)) {
        setActiveOperator(stored);
      } else if ((data || []).length !== 1) {
        setActiveOperator(null);
      }
      return data || [];
    } catch (err) {
      setOperators([]);
      setActiveOperator(null);
      return [];
    }
  }, [setActiveOperator]);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        withCredentials: true
      });
      setUser(data);
      await loadOperators(data);
    } catch (error) {
      if (error.response?.status === 401) {
        try {
          await axios.post(`${BACKEND_URL}/api/auth/refresh`, {}, {
            withCredentials: true
          });
          const { data } = await axios.get(`${BACKEND_URL}/api/auth/me`, {
            withCredentials: true
          });
          setUser(data);
          await loadOperators(data);
          return;
        } catch (refreshError) {
          setUser(false);
          setOperators([]);
          setActiveOperator(null);
        }
      } else {
        setUser(false);
        setOperators([]);
        setActiveOperator(null);
      }
    } finally {
      setLoading(false);
    }
  }, [loadOperators, setActiveOperator]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(
      `${BACKEND_URL}/api/auth/login`,
      { email, password },
      { withCredentials: true }
    );
    setUser(data);
    setActiveOperator(null);
    await loadOperators(data);
    return data;
  }, [loadOperators, setActiveOperator]);

  const logout = useCallback(async () => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/auth/logout`,
        {},
        { withCredentials: true }
      );
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(false);
    setOperators([]);
    setActiveOperator(null);
  }, [setActiveOperator]);

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      logout,
      checkAuth,
      operators,
      activeOperator,
      setActiveOperator,
      loadOperators,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
